// src/lib/realityCheckService.ts
//
// BekalNanti — Milestone P6: Shared Reality Check Layer Service
// 100% Pure Deterministic Evaluation Engine. ZERO AI, ZERO external dependencies.
// Core principle: The system calculates (P5). Reality Check evaluates the trajectory (P6).

import { GoalRecord, JourneyData, PersonRecord } from '../journey/types'
import { isEducationGoal } from './goalCapability'
import { runGoalCalculation } from './calculationService'
import { CalculationAssumptions, CalculationResult } from './calculation/types'
import { formatIDR } from './financialCalculation'
import { formatRelationship } from './peopleService'
import {
  RealityCheckResult,
  RealityCheckStatus,
  RealityCheckSignal,
  RealityCheckUncertainty,
  RealityCheckAssumption,
  RealityCheckMissingInfo,
  RealityCheckSnapshot,
} from './realityCheckTypes'

/**
 * Pure evaluation function assessing the current trajectory of a goal.
 * Strictly adheres to P6 semantic principles:
 * - Confirmed funding is primary (estimated funding is secondary scenario only)
 * - Unknown costs prevent false 'fully_funded' assertions
 * - Target in current year is a timeline signal, not arbitrary 'critical_gap'
 * - Multi-goal household capacity notice is displayed without arbitrary deduction
 * - Non-education goals report honest insufficient_information
 */
export function evaluatePlanReality(
  goal: GoalRecord,
  journey: JourneyData,
  assumptions?: Partial<CalculationAssumptions>
): RealityCheckResult {
  // 1. Resolve Person Context
  let person: PersonRecord | null = null
  if (goal.personId) {
    person = journey.persons.find((p) => p.id === goal.personId) || null
  }
  const personName = person ? person.fullName : 'Keluarga Bersama'
  const personRelationship = person ? formatRelationship(person.relationship) : null

  // 2. Count Other Active Goals in Household
  const otherActiveGoals = journey.goals.filter((g) => g.id !== goal.id && g.status !== 'archived')
  const otherActiveGoalsCount = otherActiveGoals.length

  const householdNetCapacity = journey.financialReality?.summary.netMonthlyCapacity ?? 0
  const liquidSavings =
    journey.financialReality?.summary.availableSavings ??
    journey.financialReality?.summary.totalSavings ??
    0

  const hasFinancialData =
    Boolean(journey.financialStatus?.hasIncomes) || Boolean(journey.financialStatus?.hasExpenses)

  // 3. Check for Not-Started Planning Context
  const isEducation = isEducationGoal(goal.goalType)
  const hasTarget = Boolean(goal.target)
  const hasCostItems = (goal.costItems || []).length > 0

  if (!hasFinancialData && !hasTarget && !hasCostItems) {
    return buildNotStartedResult(
      goal,
      personName,
      personRelationship,
      otherActiveGoalsCount,
      householdNetCapacity,
      liquidSavings
    )
  }

  // 4. Handle Non-Education Goals that have no target or cost items yet
  if (!isEducation && !hasTarget && !hasCostItems) {
    return buildNonEducationResult(
      goal,
      personName,
      personRelationship,
      hasFinancialData,
      otherActiveGoalsCount,
      householdNetCapacity,
      liquidSavings
    )
  }

  // 5. Evaluate Goal via P5 Calculation Engine
  const calc: CalculationResult = runGoalCalculation(goal, journey, assumptions)

  if (!calc.isCalculable) {
    return buildIncompleteEducationResult(
      goal,
      personName,
      personRelationship,
      calc,
      otherActiveGoalsCount,
      householdNetCapacity,
      liquidSavings
    )
  }

  // 6. Primary Metrics (Strictly Confirmed-Based)
  const projectedCost = calc.costs.totalProjectedCost
  const nominalCost = calc.costs.totalNominalCost
  const confirmedFunding = calc.funding.confirmedFunding
  const confirmedGap = calc.gap.gapAfterConfirmedFunding
  const estimatedFunding = calc.funding.estimatedFunding
  const gapAfterEstimated = calc.gap.gapAfterEstimatedFunding
  const unknownFundingCount = calc.funding.unknownFundingCount
  const unknownCostCount = calc.costs.unknownCostCount

  const requiredMonthlySavingConfirmedGap = calc.savingRequirement.requiredMonthlySavingConfirmedGap
  const requiredMonthlySavingEstimatedGap = calc.savingRequirement.requiredMonthlySavingEstimatedGap
  const monthsUntilTarget = calc.assumptions.monthsUntilTarget
  const yearsUntilTarget = calc.assumptions.yearsUntilTarget

  const effectiveCapacity = calc.capacity.currentMonthlyNetCapacity
  const effectiveLiquidSavings = calc.capacity.currentLiquidSavings

  const monthlyCapacityDifference = effectiveCapacity - requiredMonthlySavingConfirmedGap
  const hasCapacityDeficit = monthlyCapacityDifference < 0

  // 7. Deterministic Waterfall Status Determination
  let status: RealityCheckStatus = 'funding_gap'
  let statusLabel = 'Gap Pendanaan'
  let headline = 'Perlu Penyiapan Dana Pasti'
  let summaryStatement = ''

  if (confirmedGap === 0 && unknownCostCount === 0) {
    status = 'fully_funded'
    statusLabel = 'Terpenuhi Pasti'
    headline = 'Kebutuhan Dana Sudah Terjamin'
    summaryStatement = `Seluruh proyeksi biaya rencana (${formatIDR(projectedCost)}) telah tertutupi oleh sumber pendanaan pasti yang telah terkonfirmasi.`
  } else if (confirmedGap === 0 && unknownCostCount > 0) {
    // Cannot claim fully funded if material cost items remain unknown
    status = 'funding_gap'
    statusLabel = 'Biaya Belum Lengkap'
    headline = 'Sebagian Komponen Biaya Belum Diketahui'
    summaryStatement = `Dana terkonfirmasi saat ini telah mencukupi komponen biaya yang sudah diketahui, namun masih terdapat ${unknownCostCount} komponen biaya yang belum memiliki estimasi nominal.`
  } else if (hasCapacityDeficit) {
    status = 'funding_pressure'
    statusLabel = 'Tekanan Kapasitas'
    headline = 'Komitmen Bulanan Melebihi Arus Kas'
    summaryStatement = `Untuk menutup gap pendanaan pasti sebesar ${formatIDR(confirmedGap)}, dibutuhkan tabungan ${formatIDR(requiredMonthlySavingConfirmedGap)}/bulan. Angka ini melampaui sisa kapasitas arus kas keluarga saat ini (${formatIDR(effectiveCapacity)}/bulan) dengan defisit ${formatIDR(Math.abs(monthlyCapacityDifference))}/bulan.`
  } else if (requiredMonthlySavingConfirmedGap > 0 && !hasCapacityDeficit) {
    status = 'manageable_commitment'
    statusLabel = 'Kapasitas Mencukupi'
    headline = 'Kebutuhan Tabungan Masih Terjangkau'
    summaryStatement = `Gap pendanaan pasti sebesar ${formatIDR(confirmedGap)} membutuhkan alokasi tabungan ${formatIDR(requiredMonthlySavingConfirmedGap)}/bulan. Kapasitas arus kas keluarga (${formatIDR(effectiveCapacity)}/bulan) saat ini mencukupi dengan sisa ruang aman ${formatIDR(monthlyCapacityDifference)}/bulan.`
  } else {
    status = 'funding_gap'
    statusLabel = 'Gap Pendanaan'
    headline = 'Terdapat Gap Pendanaan Pasti'
    summaryStatement = `Terdapat gap pendanaan pasti sebesar ${formatIDR(confirmedGap)} yang perlu dipersiapkan untuk mencapai sasaran rencana ini.`
  }

  // 8. Generate Traceable Signals
  const signals: RealityCheckSignal[] = []

  // Signal 1: Confirmed Funding
  if (confirmedFunding > 0) {
    signals.push({
      id: 'sig-confirmed-funding',
      title: 'Dana Terkonfirmasi Pasti',
      statement: `Telah tersedia dana pasti sebesar ${formatIDR(confirmedFunding)} yang siap dialokasikan untuk rencana ini.`,
      severity: 'positive',
      category: 'funding',
      traceableSource: 'P5.funding.confirmedFunding',
    })
  } else {
    signals.push({
      id: 'sig-no-confirmed-funding',
      title: 'Belum Ada Dana Pasti',
      statement: 'Belum ada sumber pendanaan yang berstatus pasti/terkonfirmasi untuk rencana ini.',
      severity: 'attention',
      category: 'funding',
      traceableSource: 'P5.funding.confirmedFunding',
    })
  }

  // Signal 2: Confirmed Gap
  if (confirmedGap > 0) {
    signals.push({
      id: 'sig-confirmed-gap',
      title: 'Kebutuhan Dana Tambahan',
      statement: `Gap pendanaan pasti yang harus dipenuhi adalah sebesar ${formatIDR(confirmedGap)}.`,
      severity: 'warning',
      category: 'funding',
      traceableSource: 'P5.gap.gapAfterConfirmedFunding',
    })
  }

  // Signal 3: Estimated / Scenario Funding (Strictly secondary)
  if (estimatedFunding > 0) {
    signals.push({
      id: 'sig-estimated-funding-scenario',
      title: 'Potensi Dana Estimasi / Beasiswa',
      statement: `Terdapat potensi dana estimasi sebesar ${formatIDR(estimatedFunding)}. Jika terealisasi, dana ini dapat memperkecil sisa gap menjadi ${formatIDR(gapAfterEstimated)} (atau tabungan ${formatIDR(requiredMonthlySavingEstimatedGap)}/bulan).`,
      severity: 'neutral',
      category: 'funding',
      traceableSource: 'P5.funding.estimatedFunding',
    })
  }

  // Signal 4: Monthly Capacity vs Requirement
  if (requiredMonthlySavingConfirmedGap > 0) {
    if (hasCapacityDeficit) {
      signals.push({
        id: 'sig-capacity-deficit',
        title: 'Defisit Kapasitas Bulanan',
        statement: `Kebutuhan tabungan ${formatIDR(requiredMonthlySavingConfirmedGap)}/bulan melampaui kapasitas arus kas keluarga saat ini (${formatIDR(effectiveCapacity)}/bulan).`,
        severity: 'warning',
        category: 'capacity',
        traceableSource: 'P5.comparison.numericalDifference',
      })
    } else {
      signals.push({
        id: 'sig-capacity-surplus',
        title: 'Kapasitas Arus Kas Mendukung',
        statement: `Kapasitas arus kas bulanan keluarga (${formatIDR(effectiveCapacity)}/bulan) mencukupi alokasi tabungan yang dibutuhkan (${formatIDR(requiredMonthlySavingConfirmedGap)}/bulan).`,
        severity: 'positive',
        category: 'capacity',
        traceableSource: 'P5.comparison.numericalDifference',
      })
    }
  }

  // Signal 5: Timeline Horizon Signal
  if (yearsUntilTarget === 0) {
    signals.push({
      id: 'sig-timeline-current-year',
      title: 'Jadwal Mulai Tahun Ini',
      statement: `Tahun sasaran adalah tahun ini (${calc.assumptions.planningAnchorYear}). Waktu persiapan menabung sangat terbatas sehingga kebutuhan dana perlu disiapkan segera.`,
      severity: 'attention',
      category: 'timeline',
      traceableSource: 'P5.assumptions.yearsUntilTarget',
    })
  } else if (yearsUntilTarget > 0) {
    signals.push({
      id: 'sig-timeline-horizon',
      title: 'Horizon Waktu Persiapan',
      statement: `Tersedia waktu persiapan selama ${yearsUntilTarget} tahun (${monthsUntilTarget} bulan) hingga tahun sasaran (${calc.assumptions.targetYear}).`,
      severity: 'neutral',
      category: 'timeline',
      traceableSource: 'P5.assumptions.yearsUntilTarget',
    })
  }

  // Signal 6: Multi-Goal Household Notice
  if (otherActiveGoalsCount > 0) {
    signals.push({
      id: 'sig-multi-goal-notice',
      title: 'Kapasitas Bersama Rumah Tangga',
      statement: `Kapasitas arus kas bulanan ${formatIDR(effectiveCapacity)} merupakan kapasitas bersama rumah tangga dan dievaluasi penuh untuk rencana ini. Keluarga Anda memiliki ${otherActiveGoalsCount} rencana aktif lainnya yang juga bersumber dari kapasitas yang sama.`,
      severity: 'neutral',
      category: 'capacity',
      traceableSource: 'Journey.goals',
    })
  }

  // 9. Generate Uncertainties
  const uncertainties: RealityCheckUncertainty[] = []

  if (unknownCostCount > 0) {
    uncertainties.push({
      id: 'unc-unknown-costs',
      title: 'Komponen Biaya Belum Dikuantifikasi',
      description: `Terdapat ${unknownCostCount} komponen biaya dengan nominal yang belum diketahui. Biaya riil mungkin lebih tinggi dari proyeksi saat ini.`,
      severity: 'high',
      traceableSource: 'P5.costs.unknownCostCount',
    })
  }

  if (unknownFundingCount > 0) {
    uncertainties.push({
      id: 'unc-unknown-funding',
      title: 'Sumber Pendanaan Belum Terkonfirmasi',
      description: `Terdapat ${unknownFundingCount} sumber pendanaan yang belum memiliki kepastian nominal atau ketersediaan.`,
      severity: 'medium',
      traceableSource: 'P5.funding.unknownFundingCount',
    })
  }

  // 10. Generate Assumptions
  const assumptionsList: RealityCheckAssumption[] = [
    {
      id: 'asm-inflation',
      name: 'Inflasi Biaya Pendidikan',
      assumedValue: `${calc.assumptions.inflationPercentage}% per tahun`,
      sensitivityNote:
        'Asumsi pertumbuhan biaya tahunan. Jika inflasi riil lebih tinggi, kebutuhan dana akan meningkat.',
    },
    {
      id: 'asm-duration',
      name: 'Durasi Masa Studi',
      assumedValue: `${calc.assumptions.studyDurationMonths} bulan (${calc.assumptions.studyDurationYears} tahun)`,
      sensitivityNote: 'Digunakan sebagai pengali komponen biaya berulang (tahunan/semester/bulanan).',
    },
    {
      id: 'asm-horizon',
      name: 'Horizon Persiapan Tabungan',
      assumedValue: `${yearsUntilTarget} tahun (${monthsUntilTarget} bulan)`,
      sensitivityNote: `Dihitung dari tahun dasar perencanaan (${calc.assumptions.planningAnchorYear}) menuju tahun mulai studi (${calc.assumptions.targetYear}).`,
    },
  ]

  // 11. Missing Information
  const missingInformation: RealityCheckMissingInfo[] = []
  if (calc.costs.unknownCostCount > 0) {
    missingInformation.push({
      id: 'miss-cost-amounts',
      field: 'Nominal Komponen Biaya',
      domain: 'cost',
      impactOnEvaluation: 'Menghindari distorsi angka proyeksi biaya keseluruhan.',
    })
  }
  if (householdNetCapacity <= 0) {
    missingInformation.push({
      id: 'miss-positive-capacity',
      field: 'Surplus Arus Kas Bulanan',
      domain: 'household_finance',
      impactOnEvaluation: 'Diperlukan untuk memvalidasi kelayakan menabung rutin tanpa menambah utang baru.',
    })
  }

  // 12. Assemble Snapshot
  const snapshot: RealityCheckSnapshot = {
    totalProjectedCost: projectedCost,
    totalNominalCost: nominalCost,
    confirmedFunding,
    confirmedFundingGap: confirmedGap,
    estimatedFunding,
    gapAfterEstimatedFunding: gapAfterEstimated,
    monthsUntilTarget,
    yearsUntilTarget,
    requiredMonthlySavingConfirmedGap,
    requiredMonthlySavingEstimatedGap,
    householdNetCapacity: effectiveCapacity,
    monthlyCapacityDifference,
    hasCapacityDeficit,
    liquidSavings: effectiveLiquidSavings,
    unknownFundingCount,
    unknownCostCount,
    otherActiveGoalsCount,
  }

  return {
    goalId: goal.id,
    goalName: goal.name,
    goalType: goal.goalType,
    personId: goal.personId || null,
    personName,
    personRelationship,
    status,
    statusLabel,
    headline,
    summaryStatement,
    isCalculable: true,
    signals,
    uncertainties,
    assumptions: assumptionsList,
    missingInformation,
    snapshot,
  }
}

// ---------------------------------------------------------------------------
// Helper: Not-Started Result
// ---------------------------------------------------------------------------
function buildNotStartedResult(
  goal: GoalRecord,
  personName: string,
  personRelationship: string | null,
  otherActiveGoalsCount: number,
  householdNetCapacity: number,
  liquidSavings: number
): RealityCheckResult {
  return {
    goalId: goal.id,
    goalName: goal.name,
    goalType: goal.goalType,
    personId: goal.personId || null,
    personName,
    personRelationship,
    status: 'not_started',
    statusLabel: 'Belum Dimulai',
    headline: 'Data Perencanaan Belum Diisi',
    summaryStatement: 'Rencana ini belum memiliki data sasaran, estimasi biaya, atau pemetaan keuangan keluarga.',
    isCalculable: false,
    signals: [
      {
        id: 'sig-not-started',
        title: 'Langkah Awal Diperlukan',
        statement: 'Mulai dengan melengkapi kondisi keuangan keluarga dan rincian sasaran rencana.',
        severity: 'attention',
        category: 'cost',
        traceableSource: 'Goal.target',
      },
    ],
    uncertainties: [],
    assumptions: [],
    missingInformation: [
      {
        id: 'miss-target',
        field: 'Spesifikasi Sasaran & Tahun Mulai',
        domain: 'target',
        impactOnEvaluation: 'Menentukan horizon waktu dan estimasi biaya.',
      },
      {
        id: 'miss-finances',
        field: 'Arus Kas Keuangan Keluarga',
        domain: 'household_finance',
        impactOnEvaluation: 'Menentukan kapasitas kemampuan menabung bulanan.',
      },
    ],
    snapshot: {
      totalProjectedCost: 0,
      totalNominalCost: 0,
      confirmedFunding: 0,
      confirmedFundingGap: 0,
      estimatedFunding: 0,
      gapAfterEstimatedFunding: 0,
      monthsUntilTarget: 0,
      yearsUntilTarget: 0,
      requiredMonthlySavingConfirmedGap: 0,
      requiredMonthlySavingEstimatedGap: 0,
      householdNetCapacity,
      monthlyCapacityDifference: householdNetCapacity,
      hasCapacityDeficit: false,
      liquidSavings,
      unknownFundingCount: 0,
      unknownCostCount: 0,
      otherActiveGoalsCount,
    },
  }
}

// ---------------------------------------------------------------------------
// Helper: Non-Education Honest Result
// ---------------------------------------------------------------------------
function buildNonEducationResult(
  goal: GoalRecord,
  personName: string,
  personRelationship: string | null,
  hasFinancialData: boolean,
  otherActiveGoalsCount: number,
  householdNetCapacity: number,
  liquidSavings: number
): RealityCheckResult {
  return {
    goalId: goal.id,
    goalName: goal.name,
    goalType: goal.goalType,
    personId: goal.personId || null,
    personName,
    personRelationship,
    status: 'insufficient_information',
    statusLabel: 'Informasi Terbatas',
    headline: 'Modul Sasaran Khusus Belum Aktif',
    summaryStatement: `Rencana "${goal.name}" tersimpan aman di akun keluarga Anda. Evaluasi trajektori mendalam saat ini difokuskan pada rencana Pendidikan. Modul khusus untuk kategori ini sedang disiapkan.`,
    isCalculable: false,
    signals: [
      {
        id: 'sig-non-edu-stored',
        title: 'Rencana Tersimpan Aman',
        statement: `Rencana dikaitkan untuk ${personName}${personRelationship ? ` (${personRelationship})` : ''} dengan prioritas ${goal.priority === 'high' ? 'Tinggi' : goal.priority === 'low' ? 'Rendah' : 'Menengah'}.`,
        severity: 'neutral',
        category: 'assumption',
        traceableSource: 'Goal.status',
      },
      ...(hasFinancialData
        ? [
            {
              id: 'sig-non-edu-capacity',
              title: 'Kapasitas Keuangan Tersedia',
              statement: `Keluarga memiliki kapasitas arus kas bersih sebesar ${formatIDR(householdNetCapacity)}/bulan yang siap dialokasikan setelah modul spesifikasi aktif.`,
              severity: 'positive' as const,
              category: 'capacity' as const,
              traceableSource: 'Journey.financialReality',
            },
          ]
        : []),
    ],
    uncertainties: [],
    assumptions: [],
    missingInformation: [
      {
        id: 'miss-non-edu-model',
        field: 'Spesifikasi Biaya Kategori Ini',
        domain: 'cost',
        impactOnEvaluation: 'Dibutuhkan untuk memproyeksikan target nominal dan kurva tabungan.',
      },
    ],
    snapshot: {
      totalProjectedCost: 0,
      totalNominalCost: 0,
      confirmedFunding: 0,
      confirmedFundingGap: 0,
      estimatedFunding: 0,
      gapAfterEstimatedFunding: 0,
      monthsUntilTarget: 0,
      yearsUntilTarget: 0,
      requiredMonthlySavingConfirmedGap: 0,
      requiredMonthlySavingEstimatedGap: 0,
      householdNetCapacity,
      monthlyCapacityDifference: householdNetCapacity,
      hasCapacityDeficit: false,
      liquidSavings,
      unknownFundingCount: 0,
      unknownCostCount: 0,
      otherActiveGoalsCount,
    },
  }
}

// ---------------------------------------------------------------------------
// Helper: Incomplete Education Result
// ---------------------------------------------------------------------------
function buildIncompleteEducationResult(
  goal: GoalRecord,
  personName: string,
  personRelationship: string | null,
  calc: CalculationResult,
  otherActiveGoalsCount: number,
  householdNetCapacity: number,
  liquidSavings: number
): RealityCheckResult {
  const missingInformation: RealityCheckMissingInfo[] = (calc.blockingReasons || []).map((reason, idx) => ({
    id: `block-${idx}`,
    field: reason,
    domain: 'target',
    impactOnEvaluation: 'Data ini mutlak diperlukan sebelum evaluasi kelayakan dapat dihitung secara akurat.',
  }))

  return {
    goalId: goal.id,
    goalName: goal.name,
    goalType: goal.goalType,
    personId: goal.personId || null,
    personName,
    personRelationship,
    status: 'insufficient_information',
    statusLabel: 'Data Belum Lengkap',
    headline: 'Data Kunci Belum Terisi Lengkap',
    summaryStatement: 'Evaluasi kesiapan belum dapat dijalankan karena beberapa data kunci pada sasaran atau komponen biaya masih belum tersedia.',
    isCalculable: false,
    signals: [
      {
        id: 'sig-incomplete-inputs',
        title: 'Perlu Pengisian Data Lanjutan',
        statement: calc.blockingReasons[0] || 'Lengkapi data sasaran dan komponen biaya.',
        severity: 'attention',
        category: 'cost',
        traceableSource: 'P5.blockingReasons',
      },
    ],
    uncertainties: [],
    assumptions: [],
    missingInformation,
    snapshot: {
      totalProjectedCost: 0,
      totalNominalCost: 0,
      confirmedFunding: 0,
      confirmedFundingGap: 0,
      estimatedFunding: 0,
      gapAfterEstimatedFunding: 0,
      monthsUntilTarget: 0,
      yearsUntilTarget: 0,
      requiredMonthlySavingConfirmedGap: 0,
      requiredMonthlySavingEstimatedGap: 0,
      householdNetCapacity,
      monthlyCapacityDifference: householdNetCapacity,
      hasCapacityDeficit: false,
      liquidSavings,
      unknownFundingCount: 0,
      unknownCostCount: 0,
      otherActiveGoalsCount,
    },
  }
}
